/**
 * Public Shelf detail page at /shelf/:owner/:slug.
 * Shows all pins in a curated collection. Owner sees private pins too.
 */
import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, BookMarked, Globe, Lock } from "lucide-react";
import { api } from "../lib/api";
import { isRenderableCardData } from "../components/WeeklyItemCardThumb";

export default function ShelfPage() {
  const { owner, slug } = useParams();
  const [shelf, setShelf] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/shelves/${slug}`, { params: { owner } });
        if (!cancelled) setShelf(data);
      } catch (err) {
        if (!cancelled) setError(err?.response?.status === 404 ? "not_found" : "error");
      }
    })();
    return () => { cancelled = true; };
  }, [owner, slug]);

  if (error === "not_found") {
    return (
      <div data-testid="shelf-missing" className="min-h-[calc(100vh-80px)] flex items-center justify-center px-6 py-20">
        <div className="text-center max-w-md">
          <div className="label mb-4">Not found</div>
          <h1 className="serif text-3xl mb-4">This shelf isn&apos;t available.</h1>
          <p className="text-muted-foreground mb-8">
            It may be private, or its owner hasn&apos;t made their profile public.
          </p>
          <Link to="/archive" className="btn-gold inline-flex items-center gap-2">
            <ArrowLeft size={14} /> Back to the archive
          </Link>
        </div>
      </div>
    );
  }

  if (error || !shelf) {
    return (
      <div className="min-h-[calc(100vh-80px)] flex items-center justify-center px-6 py-20">
        <p className="serif italic text-muted-foreground">
          {error ? "Something went wrong loading this shelf." : "Loading…"}
        </p>
      </div>
    );
  }

  const ownerHandle = shelf.owner?.handle;
  const ownerUrl = ownerHandle ? `/u/${ownerHandle}` : null;

  return (
    <div data-testid="shelf-page" className="min-h-[calc(100vh-80px)]">
      <div className="max-w-4xl mx-auto px-6 sm:px-10 py-16 sm:py-24">
        <header className="mb-12">
          <Link to={ownerUrl || "/archive"} className="reaction-pill mb-8 inline-flex" data-testid="shelf-back-to-profile">
            <ArrowLeft size={12} />
            {ownerUrl ? `Back to @${ownerHandle}` : "Back to the archive"}
          </Link>
          <div className="label mb-3 flex items-center gap-2">
            <BookMarked size={11} />
            {shelf.visibility === "public" ? "Public shelf" : <><Lock size={11} /> Private shelf</>}
          </div>
          <h1 className="serif text-4xl sm:text-5xl leading-tight">{shelf.name}</h1>
          {shelf.description && (
            <p className="serif italic text-lg text-muted-foreground mt-5 max-w-2xl">
              {shelf.description}
            </p>
          )}
          <div className="mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground mt-6 flex flex-wrap gap-x-4">
            <span>{shelf.item_count ?? shelf.pin_count} item{(shelf.item_count ?? shelf.pin_count) !== 1 ? "s" : ""}</span>
            {shelf.owner?.name && (
              <span>
                Curated by{" "}
                {ownerUrl ? (
                  <Link to={ownerUrl} className="hover:text-foreground">
                    {ownerHandle ? `@${ownerHandle}` : shelf.owner.name}
                  </Link>
                ) : shelf.owner.name}
              </span>
            )}
          </div>
        </header>

        {(shelf.pins.length === 0 && (shelf.saves || []).length === 0) ? (
          <p className="serif italic text-muted-foreground py-12 text-center">
            Nothing on this collection yet.
          </p>
        ) : (
          <ul className="space-y-5">
            {shelf.pins.map((p) => (
              <li key={p.id} data-testid={`shelf-pin-${p.id}`} className="hairline p-5 hover:border-[hsl(var(--primary))] transition-colors">
                <Link to={`/concept/${p.concept_id}`} className="block group">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="mono text-[9px] uppercase tracking-[0.18em] text-muted-foreground mb-2">Concept</div>
                      <h3 className="serif text-2xl text-foreground group-hover:text-[hsl(var(--primary))] transition-colors">
                        {p.concept_title}
                      </h3>
                      {p.concept_summary && (
                        <p className="serif italic text-muted-foreground mt-2 leading-relaxed line-clamp-2">
                          {p.concept_summary}
                        </p>
                      )}
                      {p.note && (
                        <p className="mono text-[11px] uppercase tracking-[0.15em] text-[hsl(var(--accent))] mt-4">
                          Note: {p.note}
                        </p>
                      )}
                    </div>
                    {p.visibility === "private" ? <Lock size={13} className="text-muted-foreground mt-1" /> : <Globe size={13} className="text-muted-foreground mt-1" />}
                  </div>
                </Link>
              </li>
            ))}
            {(shelf.saves || []).map((s) => (
              <li key={s.id} data-testid={`shelf-save-${s.id}`} className="hairline p-5 hover:border-[hsl(var(--primary))] transition-colors">
                <Link to={s.visibility === "public" ? `/weekly#item-${s.id}` : "/settings#you"} className="block group">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="mono text-[9px] uppercase tracking-[0.18em] text-muted-foreground mb-2">Correlation</div>
                      <h3 className="serif text-2xl text-foreground group-hover:text-[hsl(var(--primary))] transition-colors">
                        {s.title}
                      </h3>
                      {isRenderableCardData(s.card_data) ? (
                        null
                      ) : s.bullets && s.bullets.length > 0 ? (
                        <ul className="mt-3 space-y-1">
                          {s.bullets.slice(0, 3).map((b, i) => (
                            <li key={`${s.id}-b-${i}`} className="flex gap-2 text-sm text-muted-foreground leading-relaxed">
                              <span className="text-[hsl(var(--primary))] mt-0.5">◇</span>
                              <span>{b}</span>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        s.response && (
                          <p className="serif italic text-muted-foreground mt-2 leading-relaxed line-clamp-3">
                            {s.response}
                          </p>
                        )
                      )}
                    </div>
                    {s.visibility === "private" ? <Lock size={13} className="text-muted-foreground mt-1" /> : <Globe size={13} className="text-muted-foreground mt-1" />}
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
